import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, DollarSign, Check, TrendingUp, Package, Percent, CreditCard, Copy, FileText, Mail, Rocket, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import { MonetizationStepper } from "@/components/monetization/MonetizationStepper";

interface PricingData {
  recommendedPrice: {
    low: { price: number; positioning: string };
    mid: { price: number; positioning: string };
    premium: { price: number; positioning: string };
  };
  suggestedPrice: number;
  rationale: string;
  psychologicalTactics: string[];
  launchStrategy: {
    earlyBird: number;
    launchPrice: number;
    evergreenPrice: number;
  };
  bundles: { name: string; includes: string[]; price: number; savings: string }[];
  upsells: { name: string; price: number; description: string }[];
  downsells: { name: string; price: number; description: string }[];
  paymentPlans: { payments: number; amount: number; total: number }[];
}

export default function MonetizationPricing() {
  const { projectId, productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [product, setProduct] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [selectedTier, setSelectedTier] = useState<"low" | "mid" | "premium">("mid");

  useEffect(() => {
    if (projectId && productId) {
      fetchData();
    }
  }, [projectId, productId]);

  const fetchData = async () => {
    try {
      const [projectRes, productRes] = await Promise.all([
        supabase.from("monetization_projects").select("*").eq("id", projectId).single(),
        supabase.from("monetization_products").select("*").eq("id", productId).single(),
      ]);

      if (projectRes.error) throw projectRes.error;
      if (productRes.error) throw productRes.error;

      setProject(projectRes.data);
      setProduct(productRes.data);

      // Check if pricing already exists
      if (productRes.data.pricing && typeof productRes.data.pricing === 'object' && !Array.isArray(productRes.data.pricing) && Object.keys(productRes.data.pricing).length > 0) {
        setPricing(productRes.data.pricing as unknown as PricingData);
      } else {
        await generatePricing(productRes.data, projectRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const generatePricing = async (productData: any, projectData: any, retryCount = 0) => {
    const maxRetries = 3;
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "generate-pricing",
          params: {
            productName: productData.title,
            productType: productData.product_type,
            niche: projectData.niche,
            audience: projectData.audience_type,
            platform: projectData.platform,
          },
        },
      });

      if (error) {
        // Check for network/timeout errors and retry
        const isNetworkError = error.message?.includes("Network") || 
                               error.message?.includes("connection") ||
                               error.message?.includes("timeout") ||
                               error.message?.includes("500");
        
        if (isNetworkError && retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
          console.log(`Network error, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
          toast.info(`Connection interrupted, retrying... (${retryCount + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return generatePricing(productData, projectData, retryCount + 1);
        }
        throw error;
      }

      setPricing(data);

      // Save to database
      await supabase
        .from("monetization_products")
        .update({ pricing: data, status: "complete" })
        .eq("id", productId);

      await supabase
        .from("monetization_projects")
        .update({ last_step: "complete" })
        .eq("id", projectId);

    } catch (error) {
      console.error("Error generating pricing:", error);
      toast.error("Failed to generate pricing strategy. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleComplete = () => {
    toast.success("Product complete! 🎉");
    navigate("/app/monetization-ai");
  };

  if (loading || generating) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-green-500" />
        <p className="text-muted-foreground">
          {generating ? "Generating pricing strategy..." : "Loading..."}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(`/app/monetization-ai/project/${projectId}/build/${productId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold">Pricing Strategy</h1>
            <p className="text-muted-foreground">{product?.title}</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="ml-14">
          <MonetizationStepper currentStep="pricing" />
        </div>

        {pricing && (
          <>
            {/* Price Tiers */}
            <div className="glass-card p-6 border-green-500/20 mb-6">
              <h3 className="font-display text-lg font-semibold mb-4">Recommended Price Points</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {(["low", "mid", "premium"] as const).map((tier) => {
                  const tierData = pricing.recommendedPrice[tier];
                  const isSelected = selectedTier === tier;
                  const isSuggested = tierData.price === pricing.suggestedPrice;
                  
                  return (
                    <div
                      key={tier}
                      onClick={() => setSelectedTier(tier)}
                      className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? "border-green-500 bg-green-500/10"
                          : "border-border/50 hover:border-green-500/50"
                      }`}
                    >
                      {isSuggested && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-background text-xs font-semibold rounded-full">
                          Recommended
                        </div>
                      )}
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground capitalize mb-2">{tier}</p>
                        <div className="text-4xl font-bold text-foreground mb-2">
                          ${tierData.price}
                        </div>
                        <p className="text-sm text-muted-foreground">{tierData.positioning}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 p-4 rounded-lg bg-muted/50">
                <p className="text-sm"><span className="font-medium">Rationale:</span> {pricing.rationale}</p>
              </div>
            </div>

            {/* Launch Strategy */}
            <div className="glass-card p-6 border-green-500/20 mb-6">
              <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Launch Strategy
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-xs text-primary mb-1">Early Bird</p>
                  <p className="text-2xl font-bold">${pricing.launchStrategy.earlyBird}</p>
                  <p className="text-xs text-muted-foreground mt-1">Limited spots / First 24-48hrs</p>
                </div>
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-xs text-green-400 mb-1">Launch Price</p>
                  <p className="text-2xl font-bold">${pricing.launchStrategy.launchPrice}</p>
                  <p className="text-xs text-muted-foreground mt-1">During launch week</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <p className="text-xs text-muted-foreground mb-1">Evergreen Price</p>
                  <p className="text-2xl font-bold">${pricing.launchStrategy.evergreenPrice}</p>
                  <p className="text-xs text-muted-foreground mt-1">Standard price after launch</p>
                </div>
              </div>
            </div>

            {/* Psychological Tactics */}
            <div className="glass-card p-6 border-green-500/20 mb-6">
              <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
                <Percent className="w-5 h-5 text-green-400" />
                Psychological Pricing Tactics
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {pricing.psychologicalTactics.map((tactic, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <p className="text-sm">{tactic}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Bundles */}
            {pricing.bundles && pricing.bundles.length > 0 && (
              <div className="glass-card p-6 border-green-500/20 mb-6">
                <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-400" />
                  Bundle Ideas
                </h3>
                <div className="space-y-3">
                  {pricing.bundles.map((bundle, i) => (
                    <div key={i} className="p-4 rounded-lg border border-border/50 flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{bundle.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Includes: {bundle.includes.join(", ")}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-400">${bundle.price}</p>
                        <p className="text-xs text-muted-foreground">{bundle.savings}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upsells & Downsells */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {pricing.upsells && pricing.upsells.length > 0 && (
                <div className="glass-card p-6 border-green-500/20">
                  <h3 className="font-display text-lg font-semibold mb-4">Upsell Ideas</h3>
                  <div className="space-y-3">
                    {pricing.upsells.map((upsell, i) => (
                      <div key={i} className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium">{upsell.name}</p>
                          <p className="text-green-400 font-semibold">${upsell.price}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">{upsell.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {pricing.downsells && pricing.downsells.length > 0 && (
                <div className="glass-card p-6 border-green-500/20">
                  <h3 className="font-display text-lg font-semibold mb-4">Downsell Ideas</h3>
                  <div className="space-y-3">
                    {pricing.downsells.map((downsell, i) => (
                      <div key={i} className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium">{downsell.name}</p>
                          <p className="text-muted-foreground font-semibold">${downsell.price}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">{downsell.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Payment Plans */}
            {pricing.paymentPlans && pricing.paymentPlans.length > 0 && (
              <div className="glass-card p-6 border-green-500/20 mb-8">
                <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-400" />
                  Payment Plan Options
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {pricing.paymentPlans.map((plan, i) => (
                    <div key={i} className="p-4 rounded-lg border border-border/50 text-center">
                      <p className="text-2xl font-bold">{plan.payments}x ${plan.amount}</p>
                      <p className="text-sm text-muted-foreground">Total: ${plan.total}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Product Dashboard — Next Steps */}
        <div className="glass-card p-6 border-green-500/20 mb-8">
          <h3 className="font-display text-lg font-semibold mb-4">What's Next?</h3>
          <div className="flex items-center gap-2 mb-4 text-xs">
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-400"><Check className="w-3 h-3" /> Product</span>
            <span className="w-4 h-px bg-muted" />
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground"><FileText className="w-3 h-3" /> Sales Page</span>
            <span className="w-4 h-px bg-muted" />
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground"><Mail className="w-3 h-3" /> Email Funnel</span>
            <span className="w-4 h-px bg-muted" />
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground"><Rocket className="w-3 h-3" /> Launch</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Button
              variant="outline"
              className="justify-start gap-2"
              onClick={() => {
                const params = new URLSearchParams();
                if (product?.title) params.set("title", product.title);
                if (product?.product_type) params.set("type", product.product_type);
                if (product?.why_it_fits) params.set("benefits", product.why_it_fits);
                if (project?.audience_type) params.set("audience", project.audience_type);
                if (pricing?.suggestedPrice) params.set("price", String(pricing.suggestedPrice));
                navigate(`/app/sales-page-builder?${params.toString()}`);
              }}
            >
              <FileText className="w-4 h-4 text-green-400" />
              Create Sales Page
              <ArrowRight className="w-3 h-3 ml-auto" />
            </Button>
            <Button
              variant="outline"
              className="justify-start gap-2"
              onClick={() => {
                const params = new URLSearchParams();
                if (project?.niche) params.set("niche", project.niche);
                if (project?.audience_type) params.set("audience", project.audience_type);
                navigate(`/app/email-list-builder?${params.toString()}`);
              }}
            >
              <Mail className="w-4 h-4 text-blue-400" />
              Build Email Funnel
              <ArrowRight className="w-3 h-3 ml-auto" />
            </Button>
            <Button
              variant="outline"
              className="justify-start gap-2"
              onClick={() => {
                const params = new URLSearchParams();
                if (product?.title) params.set("title", product.title);
                if (product?.product_type) params.set("type", product.product_type);
                navigate(`/app/launch-advisor?${params.toString()}`);
              }}
            >
              <Rocket className="w-4 h-4 text-purple-400" />
              Get Launch Plan
              <ArrowRight className="w-3 h-3 ml-auto" />
            </Button>
          </div>
        </div>

        {/* Complete Button */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => generatePricing(product, project)}
            disabled={generating}
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Regenerate Pricing
          </Button>
          <Button variant="hero" size="lg" onClick={handleComplete}>
            <Check className="w-5 h-5 mr-2" />
            Complete Product
          </Button>
        </div>
      </div>
    </div>
  );
}
